import React, { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import Modal from "../generic/modal";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import StatsService from "../../services/statsService";
import { RevAlert, RevBarra, RevButton, RevCard, RevChip, RevEmpty, RevField, RevFigure, RevSwitch } from "../ds";
import { formatBalance, formatPriceInput, parsePriceInput, hasStripeAccount } from "../../helpers/functions";

const MIN_PRICE = 0.5;

const currencySymbols = {
    USD: "$", // Dólar Estadounidense
    EUR: "€", // Euro
    MXN: "$" // Peso Mexicano
};

const PricingForm = ({ event, dispatchUpdate, dispatchRemovePricing, currency, user, loading }) => {
    const { t, i18n } = useTranslation();
    const history = useHistory();

    const { register, control, handleSubmit, watch, reset, setValue, formState: { errors } } = useForm({
        defaultValues: {
            // Sin precios guardados el campo sale vacío, no a 0: un 0 parece un precio.
            unitPrice: formatPriceInput(event.pricingData?.unitPrice ?? event.unitPrice ?? '', i18n.language),
            volumePricing: (event.volumePricing || []).map(range => ({ ...range, price: formatPriceInput(range.price, i18n.language) })),
            fullSelfiePrice: typeof event.fullSelfiePrice === 'number' && event.fullSelfiePrice > 0
                ? formatPriceInput(event.fullSelfiePrice, i18n.language)
                : (event.fullSelfiePrice || -1),
            photographerFee: event.photographerFee || 0
        }
    });

    // Estado local para forzar la validación
    const [forceValidation, setForceValidation] = useState(0);
    const [confirmRemovePricing, setConfirmRemovePricing] = useState(false);
    // Los errores no se enseñan de entrada: cada campo los muestra al salir de él
    // (touched) y todos aparecen al intentar guardar (submitAttempted).
    const [touched, setTouched] = useState(() => new Set());
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const markTouched = (name) => setTouched(prev => (prev.has(name) ? prev : new Set(prev).add(name)));

    const currencySymbol = (currency && currencySymbols[currency]) || currency;

    const { fields, append, remove } = useFieldArray({
        control,
        name: "volumePricing"
    });

    const volumePricing = watch("volumePricing");
    const fullSelfiePrice = watch("fullSelfiePrice");
    const unitPriceValue = watch("unitPrice");

    // Forzar validación cuando cambien los campos
    useEffect(() => {
        // Este useEffect se ejecuta cuando cambia forceValidation
        // y mantiene la validación en tiempo real
    }, [forceValidation]);

    useEffect(() => {
        if (event && event.pricingData) {
            const packPrice = event.pricingData.fullSelfiePrice;
            reset({
                unitPrice: formatPriceInput(event.pricingData.unitPrice ?? 0, i18n.language),
                volumePricing: (event.pricingData.volumePricing || null)?.map(range => ({ ...range, price: formatPriceInput(range.price, i18n.language) })) ?? null,
                fullSelfiePrice: typeof packPrice === 'number' && packPrice > 0
                    ? formatPriceInput(packPrice, i18n.language)
                    : (packPrice || null),
                photographerFee: event.pricingData.photographerFee || 0
            });
        } else if (event) {
            // Sin precios (p. ej. justo después de «Borrar precios»): el formulario se vacía.
            reset({ unitPrice: '', volumePricing: [], fullSelfiePrice: -1, photographerFee: 0 });
        }
        setTouched(new Set());
        setSubmitAttempted(false);
        // eslint-disable-next-line
    }, [event, reset]);

    const [usersAccessCount, setUsersAccessCount] = useState(undefined);

    useEffect(() => {
        let cancelled = false;
        setUsersAccessCount(undefined);

        if (!event?.id) {
            return undefined;
        }

        StatsService.getNumberOfUsersAccessByCode(event.id)
            .then(response => { if (!cancelled) setUsersAccessCount(response?.data?.Data ?? null); })
            .catch(() => { if (!cancelled) setUsersAccessCount(null); });

        return () => { cancelled = true; };
    }, [event?.id]);

    // Función para validar rangos
    const validateRanges = (ranges) => {
        // Con un solo tramo no hay continuidad que comprobar, pero su precio sí
        // (antes salía aquí y un único tramo a 0 se guardaba).
        if (!ranges || ranges.length === 0) {
            return { isValid: true, errors: [], problematicFields: [] };
        }

        const errors = [];
        const problematicFields = [];

        // Validar que no haya huecos o solapamientos entre rangos consecutivos
        for (let i = 0; i < ranges.length - 1; i++) {
            const current = ranges[i];
            const next = ranges[i + 1];

            if (current && next && current.unitsTo && next.unitsFrom) {
                if (current.unitsTo + 1 !== next.unitsFrom) {
                    errors.push(t("pricingForm.validation.range.consecutive"));
                    // Resaltar el campo "desde" del rango siguiente
                    problematicFields.push({ index: i + 1, field: 'unitsFrom' });
                    problematicFields.push({ index: i, field: 'unitsTo' });
                    break; // Solo un mensaje general
                }
            }
        }

        // Validar precios mínimos y marcar campos problemáticos
        for (let i = 0; i < ranges.length; i++) {
            const range = ranges[i];
            if (range) {
                // Validar que el precio no esté vacío
                if (!range.price || range.price === null || range.price === undefined || range.price === '') {
                    errors.push(t("pricingForm.validation.required"));
                    problematicFields.push({ index: i, field: 'price' });
                    break; // Solo un mensaje general
                }
                // Validar precio mínimo
                else if (parsePriceInput(range.price) < MIN_PRICE) {
                    errors.push(t("pricingForm.validation.range.price.min", { minPrice: MIN_PRICE }));
                    problematicFields.push({ index: i, field: 'price' });
                    break; // Solo un mensaje general
                }
            }
        }

        // Validar que en cada rango, "To" sea superior a "From"
        for (let i = 0; i < ranges.length; i++) {
            const range = ranges[i];
            if (range && range.unitsFrom !== undefined && range.unitsTo !== undefined && range.unitsTo !== -1) {
                if (Number(range.unitsTo) < Number(range.unitsFrom)) {
                    errors.push(t("pricingForm.validation.to_not_greater_than_from"));
                    problematicFields.push({ index: i, field: 'unitsTo' });
                    problematicFields.push({ index: i, field: 'unitsFrom' });
                    break; // Solo un mensaje general
                }
            }
        }

        return { isValid: errors.length === 0, errors, problematicFields };
    };

    // Función para validar precio unitario y pack selfie
    const validateOtherPrices = () => {
        const errors = [];
        const problematicFields = [];

        // Validar precio unitario
        const unitPrice = watch("unitPrice");
        if (!unitPrice || unitPrice === null || unitPrice === undefined || unitPrice === '') {
            errors.push(t("pricingForm.validation.required"));
            problematicFields.push({ field: 'unitPrice' });
        } else if (parsePriceInput(unitPrice) < MIN_PRICE) {
            errors.push(t("pricingForm.validation.price.min", { minPrice: MIN_PRICE }));
            problematicFields.push({ field: 'unitPrice' });
        }

        // Validar precio pack selfie (solo si existe)
        if (fullSelfiePrice !== -1 && fullSelfiePrice !== null && fullSelfiePrice !== undefined && fullSelfiePrice !== '') {
            if (parsePriceInput(fullSelfiePrice) < MIN_PRICE) {
                errors.push(t("pricingForm.validation.price.min", { minPrice: MIN_PRICE }));
                problematicFields.push({ field: 'fullSelfiePrice' });
            }
        }
        // Si fullSelfiePrice === -1, no validar (campo deshabilitado)

        return { isValid: errors.length === 0, errors, problematicFields };
    };



    // El formulario guarda los precios como cadena del idioma ("10,50"), que
    // Newtonsoft no sabe leer si trae coma: el payload sale siempre numérico.
    const numerizePrice = (value) => {
        const parsed = parsePriceInput(value);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const onSubmit = (data) => {
        if (!combinedValidation.isValid) {
            setSubmitAttempted(true);
            return;
        }
        const tiers = data.volumePricing && data.volumePricing.length > 0
            ? data.volumePricing.map((range, idx) => ({
                ...range,
                unitsFrom: Number(range.unitsFrom),
                // El último tramo no lleva tope
                unitsTo: idx === data.volumePricing.length - 1 ? -1 : Number(range.unitsTo),
                price: numerizePrice(range.price)
            }))
            : data.volumePricing;

        dispatchUpdate({
            ...data,
            unitPrice: numerizePrice(data.unitPrice),
            fullSelfiePrice: data.fullSelfiePrice === -1 ? -1 : numerizePrice(data.fullSelfiePrice),
            volumePricing: tiers
        });
    };

    // Deja teclear solo cifras y UN separador decimal, el que teclee el usuario
    // (la coma ya no se convierte en punto: toda lectura numérica pasa por
    // parsePriceInput, que acepta los dos). Máximo dos decimales.
    function sanitizePriceTyping(e) {
        let value = e.target.value.replace(/[^0-9.,]/g, '');
        const separatorIndex = value.search(/[.,]/);
        if (separatorIndex !== -1) {
            const integerPart = value.slice(0, separatorIndex);
            const separator = value.charAt(separatorIndex);
            const decimals = value.slice(separatorIndex + 1).replace(/[.,]/g, '').substring(0, 2);
            value = integerPart + separator + decimals;
        }
        e.target.value = value;
    }

    // Al salir del campo el valor se reescribe con dos decimales en el
    // separador del idioma. Si no se puede leer, se deja lo tecleado y habla
    // la validación.
    const handlePriceBlur = (name, registered) => (e) => {
        if (registered) {
            registered.onBlur(e);
        }
        const parsed = parsePriceInput(e.target.value);
        if (Number.isFinite(parsed)) {
            setValue(name, formatPriceInput(parsed, i18n.language), { shouldValidate: true });
        }
        markTouched(name);
        setForceValidation(prev => prev + 1);
    };

    // Validar rangos en tiempo real
    const rangeValidation = validateRanges(volumePricing || []);
    const otherPricesValidation = validateOtherPrices();

    // Validación combinada
    const combinedValidation = {
        isValid: rangeValidation.isValid && otherPricesValidation.isValid && Object.keys(errors).length === 0,
        errors: [...rangeValidation.errors, ...otherPricesValidation.errors],
        problematicFields: [...rangeValidation.problematicFields, ...otherPricesValidation.problematicFields]
    };

    // Resumen para la barra inferior, con el mismo formato que Datos basicos: una linea
    // con icono. El detalle ya esta marcado campo a campo, asi que aqui basta el recuento.
    const uniqueErrors = [...new Set(combinedValidation.errors)];
    const errorSummary = combinedValidation.isValid || !submitAttempted
        ? null
        : uniqueErrors.length > 0
            ? t('pricingForm.validation.summary', { count: uniqueErrors.length, list: uniqueErrors.join(', ') })
            : t('pricingForm.validation.summaryFields');

    // Solo los campos ya tocados, o todos tras intentar guardar. Las reglas siguen
    // calculándose en vivo, así que un campo en rojo se limpia mientras se corrige.
    const fieldName = (f) => (f.index === undefined ? f.field : `volumePricing[${f.index}].${f.field}`);
    const visibleProblems = combinedValidation.problematicFields.filter(f => submitAttempted || touched.has(fieldName(f)));
    const showsError = (field, index) => visibleProblems.some(f => f.field === field && f.index === index);
    const rhfError = (name, error) => !!error && (submitAttempted || touched.has(name));

    const unitPriceHasError = showsError('unitPrice') || rhfError('unitPrice', errors.unitPrice);
    const fullSelfiePriceHasError = showsError('fullSelfiePrice') || rhfError('fullSelfiePrice', errors.fullSelfiePrice);

    const handleAddTier = () => {
        if (!fields || fields.length === 0) {
            append({ unitsFrom: 2, unitsTo: 3, price: 0 });
            return;
        }
        const currentRange = volumePricing && volumePricing[0];
        if (currentRange) {
            const newFrom = currentRange.unitsTo && currentRange.unitsTo !== -1 ? Number(currentRange.unitsTo) + 1 : (Number(currentRange.unitsFrom) + 1);
            append({ unitsFrom: newFrom, unitsTo: newFrom + 1, price: currentRange.price || 0 });
        }
    };

    const getTierDiscount = (index) => {
        const rawBase = volumePricing && volumePricing[0] ? parsePriceInput(volumePricing[0].price) : NaN;
        const base = !Number.isNaN(rawBase) && rawBase > 0 ? rawBase : parsePriceInput(unitPriceValue);
        const rawCurrent = volumePricing && volumePricing[index] ? parsePriceInput(volumePricing[index].price) : NaN;
        if (Number.isNaN(base) || base <= 0 || Number.isNaN(rawCurrent)) {
            return { kind: 'none' };
        }
        const percent = Math.round((1 - rawCurrent / base) * 100);
        return percent > 0 ? { kind: 'discount', percent } : { kind: 'none' };
    };

    const parsedUnitPrice = parsePriceInput(unitPriceValue);
    const hasValidUnitPrice = !Number.isNaN(parsedUnitPrice) && parsedUnitPrice > 0;
    const purchaseAmountCents = hasValidUnitPrice ? Math.round(parsedUnitPrice * 100) * 3 : null;
    const stripeFeeCents = purchaseAmountCents !== null ? Math.round(25 + purchaseAmountCents * 15 / 1000) : null;
    const purchaseAmount = purchaseAmountCents !== null ? purchaseAmountCents / 100 : null;
    const stripeFee = stripeFeeCents !== null ? stripeFeeCents / 100 : null;
    const netAmount = purchaseAmountCents !== null ? Math.max(purchaseAmountCents - stripeFeeCents, 0) / 100 : null;
    const priceFormatter = (() => {
        try {
            return new Intl.NumberFormat(i18n.language, { style: 'currency', currency: currency || 'EUR' });
        } catch (error) {
            return new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' });
        }
    })();
    const formatPrice = (value) => (value === null || value === undefined ? '—' : priceFormatter.format(value));

    const priceMinRule = {
        required: t("pricingForm.validation.required"),
        validate: (value) => parsePriceInput(value) >= MIN_PRICE || t("pricingForm.validation.price.min", { minPrice: MIN_PRICE }),
        pattern: { value: /^\d+([.,]\d{1,2})?$/, message: t("pricingForm.validation.decimals.max") }
    };
    const unitPriceReg = register(`unitPrice`, priceMinRule);
    const fullSelfiePriceReg = fullSelfiePrice !== -1 ? register(`fullSelfiePrice`, priceMinRule) : null;

    const balanceDisplay = formatBalance(user?.balance, i18n.language);
    const usersAccessDisplay = (usersAccessCount === undefined || usersAccessCount === null) ? '—' : usersAccessCount;

    return (
        <>
            <div className="rev-pricing-header">
                <h2 className="rev-page-title">{t('Sales pricing')}</h2>
                <span className="rev-help">{t('Below you can set your rates for photo sales.')}</span>
                <span className="rev-help">{t('Set sales price, taxes included.')}</span>
            </div>
            <form onSubmit={handleSubmit(onSubmit, () => setSubmitAttempted(true))}>
                <div className="rev-cols">
                    <div className="rev-cols__main">

                        <RevCard title={t('pricingForm.card.photoPrice.title')}>
                            <span className="rev-help">{t('pricingForm.card.photoPrice.subtitle')}</span>
                            <div className="rev-row">
                                <RevField className="rev-field--w200" label={t('pricingForm.field.unitPrice.label')} htmlFor="unitPrice"
                                    help={unitPriceHasError ? (errors.unitPrice?.message || t("pricingForm.validation.price.min", { minPrice: MIN_PRICE })) : null} helpTone="err">
                                    <div className="rev-field-row">
                                        <input
                                            id="unitPrice"
                                            {...unitPriceReg}
                                            onChange={(e) => {
                                                sanitizePriceTyping(e);
                                                setValue(`unitPrice`, e.target.value);
                                                setForceValidation(prev => prev + 1);
                                            }}
                                            onBlur={handlePriceBlur(`unitPrice`, unitPriceReg)}
                                            className={`rev-input rev-num rev-input--prefixed rev-input--fill${unitPriceHasError ? ' rev-input--error' : ''}`}
                                            type="text"
                                        />
                                        <span className="rev-suffix">{currencySymbol} {t('pricingForm.suffix.perPhoto')}</span>
                                    </div>
                                </RevField>

                                <RevField className="rev-field--w280" label={t('pricingForm.field.fullSelfiePrice.label')} htmlFor="fullSelfiePrice"
                                    help={fullSelfiePriceHasError ? (errors.fullSelfiePrice?.message || t("pricingForm.validation.price.min", { minPrice: MIN_PRICE })) : null} helpTone="err">
                                    {fullSelfiePrice !== -1
                                        ? <div className="rev-field-row">
                                            <input
                                                id="fullSelfiePrice"
                                                {...fullSelfiePriceReg}
                                                onChange={(e) => {
                                                    sanitizePriceTyping(e);
                                                    setValue(`fullSelfiePrice`, e.target.value);
                                                    setForceValidation(prev => prev + 1);
                                                }}
                                                onBlur={handlePriceBlur(`fullSelfiePrice`, fullSelfiePriceReg)}
                                                className={`rev-input rev-num rev-input--prefixed rev-input--fill${fullSelfiePriceHasError ? ' rev-input--error' : ''}`}
                                                type="text"
                                            />
                                            <span className="rev-suffix">{currencySymbol} {t('pricingForm.suffix.perPack')}</span>
                                        </div>
                                        : <span className="rev-help">{t('Add a single price for all photos on each selfie')}</span>}
                                </RevField>

                                <RevField label={t('pricingForm.field.packActive.label')}>
                                    <label className="rev-switch-field rev-switch-field--control" htmlFor="ds-pricing-pack-active">
                                        <RevSwitch
                                            as="button"
                                            bare
                                            id="ds-pricing-pack-active"
                                            on={fullSelfiePrice !== -1}
                                            role="switch"
                                            aria-checked={fullSelfiePrice !== -1}
                                            aria-label={t('pricingForm.field.packActive.label')}
                                            onClick={() => {
                                                setValue('fullSelfiePrice', fullSelfiePrice !== -1 ? -1 : 0);
                                                setForceValidation(prev => prev + 1);
                                            }}
                                        />
                                        <span>{t('pricingForm.field.packActive.text')}</span>
                                    </label>
                                </RevField>
                            </div>
                        </RevCard>

                        <RevCard
                            title={t('pricingForm.card.volumePricing.title')}
                            head={
                                <RevButton variant="secondary" size="sm" onClick={handleAddTier}>
                                    {t('pricingForm.action.addTier')}
                                </RevButton>
                            }
                        >
                            <span className="rev-help rev-help--tight">{t('pricingForm.card.volumePricing.subtitle')}</span>

                            {fields?.length > 0 && volumePricing
                                ? <div className="rev-tramo-list">
                                    {fields.map((item, index) => {
                                        const isLastRange = index === fields.length - 1;
                                        const currentTier = (volumePricing && volumePricing[index]) || item;
                                        const fromName = `volumePricing[${index}].unitsFrom`;
                                        const toName = `volumePricing[${index}].unitsTo`;
                                        const fromReg = register(fromName, {
                                            required: t("pricingForm.validation.required"),
                                            min: { value: index === 0 ? 2 : 1, message: t("pricingForm.validation.range.firstFrom.min") },
                                            valueAsNumber: true
                                        });
                                        const toReg = !isLastRange && register(toName, {
                                            required: t("pricingForm.validation.required"),
                                            min: { value: 2, message: t("pricingForm.validation.range.to.min") },
                                            valueAsNumber: true
                                        });
                                        const fieldErrorMessages = [
                                            rhfError(fromName, errors?.volumePricing?.[index]?.unitsFrom) && errors.volumePricing[index].unitsFrom.message,
                                            rhfError(toName, errors?.volumePricing?.[index]?.unitsTo) && errors.volumePricing[index].unitsTo.message
                                        ].filter(Boolean);
                                        const isProblematic = visibleProblems.some(f => f.index === index);
                                        const tierMessages = fieldErrorMessages.length > 0 ? fieldErrorMessages : (isProblematic ? combinedValidation.errors : []);
                                        const tierHasError = fieldErrorMessages.length > 0 || isProblematic;
                                        const discount = index === 0 ? null : getTierDiscount(index);

                                        return (
                                            <div className={`rev-tramo${tierHasError ? ' rev-tramo--err' : ''}`} key={item.id}>
                                                <div className="rev-tramo__head">
                                                    <span className="rev-mono rev-tramo__n">{String(index + 1).padStart(2, '0')}</span>
                                                    <span className="rev-tramo__title">
                                                        {isLastRange
                                                            ? <Trans i18nKey="pricingForm.tier.titleOpen" values={{ from: currentTier?.unitsFrom }} components={[<span className="rev-num" key="0" />]} />
                                                            : <Trans i18nKey="pricingForm.tier.titleRange" values={{ from: currentTier?.unitsFrom, to: currentTier?.unitsTo }} components={[<span className="rev-num" key="0" />, <span className="rev-num" key="1" />]} />}
                                                    </span>
                                                    <span className="rev-tramo__actions">
                                                        {index === 0
                                                            ? <RevChip>{t('pricingForm.tier.chip.base')}</RevChip>
                                                            : discount.kind === 'discount'
                                                                ? <RevChip tone="ok">
                                                                    <Trans i18nKey="pricingForm.tier.chip.discount" values={{ n: discount.percent }} components={[<span className="rev-num" key="0" />]} />
                                                                </RevChip>
                                                                : <RevChip tone="warn">{t('pricingForm.tier.chip.noDiscount')}</RevChip>}
                                                        <RevButton variant="secondary" icon aria-label={t('pricingForm.tier.removeAria')}
                                                            onClick={() => {
                                                                remove(index);
                                                                // Los índices se desplazan: lo tocado en tramos deja de cuadrar.
                                                                setTouched(prev => new Set([...prev].filter(name => !name.startsWith('volumePricing['))));
                                                            }}>
                                                            <span className="material-icons rev-icon-sm">close</span>
                                                        </RevButton>
                                                    </span>
                                                </div>
                                                <div className="rev-tramo__grid">
                                                    <RevField className="rev-minw0" label={t('pricingForm.field.tierFrom.label')} htmlFor={`volumePricing[${index}].unitsFrom`}>
                                                        <input
                                                            id={`volumePricing[${index}].unitsFrom`}
                                                            {...fromReg}
                                                            onBlur={(e) => { fromReg.onBlur(e); markTouched(fromName); }}
                                                            disabled={isLastRange && fields.length > 1}
                                                            type="number"
                                                            className={`rev-input rev-num${isLastRange && fields.length > 1 ? ' rev-input--readonly' : ''}${showsError('unitsFrom', index) ? ' rev-input--error' : ''}`}
                                                            onChange={(e) => {
                                                                setValue(`volumePricing[${index}].unitsFrom`, Number(e.target.value));
                                                                setForceValidation(prev => prev + 1);
                                                            }}
                                                        />
                                                    </RevField>

                                                    {!isLastRange &&
                                                        <RevField className="rev-minw0" label={t('pricingForm.field.tierTo.label')} htmlFor={`volumePricing[${index}].unitsTo`}>
                                                            <input
                                                                id={`volumePricing[${index}].unitsTo`}
                                                                {...toReg}
                                                                onBlur={(e) => { toReg.onBlur(e); markTouched(toName); }}
                                                                type="number"
                                                                className={`rev-input rev-num${showsError('unitsTo', index) ? ' rev-input--error' : ''}`}
                                                                onChange={(e) => {
                                                                    setValue(`volumePricing[${index}].unitsTo`, Number(e.target.value));
                                                                    // Si es la penúltima fila, actualizar automáticamente la última
                                                                    if (index === fields.length - 2) {
                                                                        const newValue = Number(e.target.value);
                                                                        setValue(`volumePricing[${index + 1}].unitsFrom`, newValue + 1);
                                                                        setValue(`volumePricing[${index + 1}].unitsTo`, -1);
                                                                    }
                                                                    setForceValidation(prev => prev + 1);
                                                                }}
                                                            />
                                                        </RevField>}

                                                    <RevField className="rev-minw0" label={t('pricingForm.field.tierPrice.label')} htmlFor={`volumePricing[${index}].price`}>
                                                        <div className="rev-field-row">
                                                            <input
                                                                id={`volumePricing[${index}].price`}
                                                                value={volumePricing && volumePricing[index] ? volumePricing[index].price || '' : ''}
                                                                onChange={(e) => {
                                                                    sanitizePriceTyping(e);
                                                                    setValue(`volumePricing[${index}].price`, e.target.value);
                                                                    setForceValidation(prev => prev + 1);
                                                                }}
                                                                onBlur={handlePriceBlur(`volumePricing[${index}].price`, null)}
                                                                className={`rev-input rev-num rev-input--prefixed rev-input--fill${showsError('price', index) ? ' rev-input--error' : ''}`}
                                                                type="text"
                                                            />
                                                            <span className="rev-suffix">{currencySymbol} {t('pricingForm.suffix.perPhoto')}</span>
                                                        </div>
                                                    </RevField>
                                                </div>
                                                {tierMessages.length === 0 &&
                                                    <span className="rev-help rev-tramo__note">
                                                        {isLastRange
                                                            ? <Trans i18nKey="pricingForm.tier.note.open" values={{ from: currentTier?.unitsFrom }} components={[<span className="rev-num" key="0" />]} />
                                                            : Number(currentTier?.unitsTo) >= Number(currentTier?.unitsFrom) && parsePriceInput(currentTier?.price) > 0
                                                                ? <Trans i18nKey="pricingForm.tier.note.range"
                                                                    values={{ n: Number(currentTier?.unitsTo), total: formatPrice(parsePriceInput(currentTier?.price) * Number(currentTier?.unitsTo)) }}
                                                                    components={[<span className="rev-num" key="0" />, <span className="rev-num" key="1" />]} />
                                                                : t('pricingForm.tier.note.needTo')}
                                                    </span>}
                                                {tierMessages.map((message, messageIndex) => (
                                                    <span className="rev-help rev-help--err" key={messageIndex}>{message}</span>
                                                ))}
                                            </div>
                                        );
                                    })}
                                </div>
                                : <RevEmpty body={t("Add different price ranges")} />}

                            <span className="rev-help rev-help--divider">
                                {t('pricingForm.card.volumePricing.footnote')}
                            </span>
                        </RevCard>
                    </div>

                    <div className="rev-cols__aside">
                        <RevCard title={t('pricingForm.card.summary.title')}>

                            {currency === 'EUR' &&
                                <>
                                    <span className="rev-help">
                                        <Trans i18nKey="pricingForm.summary.intro" values={{ price: formatPrice(hasValidUnitPrice ? parsedUnitPrice : null) }} components={[<span className="rev-num" key="0" />]} />
                                    </span>
                                    <div className="rev-kv-row">
                                        <span className="rev-help">{t('pricingForm.summary.buyerPays')}</span>
                                        <span className="rev-num">{formatPrice(purchaseAmount)}</span>
                                    </div>
                                    <div className="rev-kv-row">
                                        <span className="rev-help">{t('pricingForm.summary.revopicFee')}</span>
                                        <span className="rev-num">{formatPrice(0)}</span>
                                    </div>
                                    <div className="rev-kv-row">
                                        <span className="rev-help">{t('pricingForm.summary.stripeFeeEU')}</span>
                                        <span className="rev-num">{stripeFee === null ? '—' : formatPrice(-stripeFee)}</span>
                                    </div>
                                    <div className="rev-summary-total">
                                        <span className="rev-summary-total__k">{t('pricingForm.summary.netToBank')}</span>
                                        <RevFigure size="sm">{formatPrice(netAmount)}</RevFigure>
                                    </div>
                                </>}

                            <span className="rev-help">
                                <strong>{t('account.sales.stripeCommissionRevopic')}</strong> {t('account.sales.stripeCommissionRevenuePercent')}
                            </span>
                            <span className="rev-help">
                                <strong>{t('account.sales.stripeCommissionStripe')}</strong> {t('account.sales.stripeCommissionEU')} / {t('account.sales.stripeCommissionUK')}
                            </span>
                            <span className="rev-help">{t('account.sales.vatNote')}</span>
                        </RevCard>

                        <RevCard title={t('pricingForm.card.credits.title')}>
                            <div className="rev-kv-row">
                                <span className="rev-help">{t('pricingForm.credits.usersAccessLabel')}</span>
                                <span className="rev-num">{usersAccessDisplay}</span>
                            </div>
                            <div className="rev-kv-row">
                                <span className="rev-help">{t('pricingForm.credits.balanceLabel')}</span>
                                <span className="rev-num">{balanceDisplay}</span>
                            </div>
                        </RevCard>
                    </div>
                </div>

                {!hasStripeAccount(user) &&
                    <Modal
                        showCancelButton
                        showConfirmButton
                        title={t('pricingForm.stripeRequired.title')}
                        message={t('pricingForm.stripeRequired.body')}
                        cancelButtonText={t('Cancel')}
                        confirmButtonText={t('pricingForm.stripeRequired.cta')}
                        onCancel={() => {
                            history.push('/events/' + event.id + '/basic');
                        }}
                        onConfirm={() => {
                            history.push('/account/photo-sale/home');
                        }}
                    />}

                {(event?.hasCarts || event?.hasOrders) && (
                    <RevAlert tone="warn" className="rev-pricing-alert">
                        <span>
                            {event?.hasCarts && event?.hasOrders
                                ? t('event.pricingForm.hasCartsAndOrders')
                                : event?.hasCarts
                                    ? t('event.pricingForm.hasCarts')
                                    : t('event.pricingForm.hasOrders')
                            }
                        </span>
                    </RevAlert>
                )}

                {confirmRemovePricing &&
                    <Modal
                        showCancelButton
                        showConfirmButton
                        isDanger
                        loading={loading}
                        title={t('pricingForm.removePricing.title')}
                        message={t('pricingForm.removePricing.body')}
                        cancelButtonText={t('Cancel')}
                        confirmButtonText={t('pricingForm.removePricing.confirm')}
                        onCancel={() => setConfirmRemovePricing(false)}
                        onConfirm={async () => {
                            try {
                                await dispatchRemovePricing();
                            } finally {
                                setConfirmRemovePricing(false);
                            }
                        }}
                    />}

                <RevBarra sangre error={errorSummary}>
                    {/* Con pedidos o carritos no se ofrece volver a gratis: lo vendido o en curso tenía precio */}
                    {event?.pricingData && !event?.hasOrders && !event?.hasCarts &&
                        <RevButton variant="danger" disabled={loading} onClick={() => setConfirmRemovePricing(true)}>
                            {t('pricingForm.actions.removePricing')}
                        </RevButton>}
                    <RevButton variant="secondary" onClick={() => history.push('/events/' + event.id + '/photos')}>
                        {t('Cancel')}
                    </RevButton>
                    <RevButton variant="primary" type="submit"
                        disabled={!hasStripeAccount(user) || loading}>
                        {t('pricingForm.actions.save')}
                    </RevButton>
                </RevBarra>
            </form>
        </>
    );
}

export default PricingForm;
